import { useEffect, useState } from "react";
import { api, ApiError, download } from "../api";
import { fmtDate, fmtINR } from "../fmt";

interface IncomeRow {
  periodMonth: string;
  kind: string;
  gross: number | null;
  net: number | null;
}
interface LoanDrawdownRow {
  startDate: string;
  lender: string;
  principal: number | null;
}
interface AssetAcquisitionRow {
  acquiredOn: string;
  name: string;
  cost: number | null;
}
interface SourceOfFundsResult {
  incomes: IncomeRow[];
  loanDrawdowns: LoanDrawdownRow[];
  assetAcquisitions: AssetAcquisitionRow[];
  summary: { totalIncome: number; totalBorrowings: number; totalAssetsAcquired: number };
}

export function SourceOfFunds() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [result, setResult] = useState<SourceOfFundsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs}` : "";
    api
      .get<SourceOfFundsResult>(`/api/source-of-funds${suffix}`)
      .then(setResult)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load source of funds"));
  }, [from, to]);

  const onExport = async () => {
    try {
      const qs = new URLSearchParams();
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const suffix = qs.toString() ? `?${qs}` : "";
      await download(`/api/source-of-funds.xlsx${suffix}`, "source-of-funds.xlsx");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "export failed");
    }
  };

  return (
    <div>
      <h1>Source of Funds</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar">
        <div className="field">
          <label htmlFor="s-from">From</label>
          <input id="s-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="s-to">To</label>
          <input id="s-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="button" onClick={onExport}>
          Export xlsx
        </button>
      </div>

      {result && (
        <div className="card-row" style={{ marginBottom: "1rem" }}>
          <div className="card stat-card">
            <div className="label">Total income</div>
            <div className="value">{fmtINR(result.summary.totalIncome)}</div>
          </div>
          <div className="card stat-card">
            <div className="label">Total borrowings</div>
            <div className="value">{fmtINR(result.summary.totalBorrowings)}</div>
          </div>
          <div className="card stat-card">
            <div className="label">Total assets acquired</div>
            <div className="value">{fmtINR(result.summary.totalAssetsAcquired)}</div>
          </div>
        </div>
      )}

      <h2>Income received</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Period</th>
              <th>Kind</th>
              <th className="num">Gross</th>
              <th className="num">Net</th>
            </tr>
          </thead>
          <tbody>
            {(result?.incomes ?? []).map((r, i) => (
              <tr key={i}>
                <td>{fmtDate(r.periodMonth)}</td>
                <td>{r.kind}</td>
                <td className="num">{fmtINR(r.gross)}</td>
                <td className="num">{fmtINR(r.net)}</td>
              </tr>
            ))}
            {result && result.incomes.length === 0 && (
              <tr>
                <td colSpan={4} className="empty-state">
                  No income in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2>Loans drawn down</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Start date</th>
              <th>Lender</th>
              <th className="num">Principal</th>
            </tr>
          </thead>
          <tbody>
            {(result?.loanDrawdowns ?? []).map((r, i) => (
              <tr key={i}>
                <td>{fmtDate(r.startDate)}</td>
                <td>{r.lender}</td>
                <td className="num">{fmtINR(r.principal)}</td>
              </tr>
            ))}
            {result && result.loanDrawdowns.length === 0 && (
              <tr>
                <td colSpan={3} className="empty-state">
                  No loans drawn down in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2>Assets acquired</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Acquired on</th>
              <th>Name</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {(result?.assetAcquisitions ?? []).map((r, i) => (
              <tr key={i}>
                <td>{fmtDate(r.acquiredOn)}</td>
                <td>{r.name}</td>
                <td className="num">{fmtINR(r.cost)}</td>
              </tr>
            ))}
            {result && result.assetAcquisitions.length === 0 && (
              <tr>
                <td colSpan={3} className="empty-state">
                  No assets acquired in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
